// @flow
import React, { useState, useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import {
  Box,
  Link,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { useMenuToggle } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { useMediaQuery } from '@fortress-technology-solutions/fortress-component-library/hooks';
import FortressLogo from '../../img/Fortress-Logo-Gray.svg';
import PropertySearchMenu from '../../components/AppBarNavDrawer/PropertySearchMenu';
import UserMenu from '../../components/AppBarNavDrawer/UserMenu';
import { getCookie } from '../../utils/cookie';

const getAppBarBgColor = (environmentName) => {
  if (environmentName === 'development') return 'secondary.dark';
  if (environmentName === 'ephemeral') return 'success.dark';
  return 'primary.darker';
};

/**
 * AccountSuspendedPage
 *
 * Displayed when a property's access has been suspended due to non-payment.
 * Visibility is controlled via the `accountSuspended` LaunchDarkly feature
 * flag (see App/index.js). LaunchDarkly's built-in targeting rules (keyed on
 * propertyId in the LD user context) scope the flag to specific properties.
 *
 * Users with access to multiple properties can switch to a non-suspended
 * property via the property selector rendered below.
 */
const AccountSuspendedPage = ({
  selectedProperty,
  userProperties,
  onSelectProperty,
  onSelectPortfolioSummary,
  logOut,
  environmentName,
}) => {
  const [fullName, setFullName] = useState('');
  const isXs = useMediaQuery('only', 'xs');
  const {
    open: userMenuOpen,
    handleToggle: handleUserMenuToggle,
    handleClose: handleUserMenuClose,
    anchorRef: userMenuAnchorRef,
  } = useMenuToggle(false);

  useEffect(() => {
    const sessionId = getCookie('session_id');
    if (sessionId?.length && sessionId !== 'undefined') {
      try {
        const decoded = JSON.parse(atob(sessionId.split('.')[1]));
        setFullName(`${decoded.firstName} ${decoded.lastName}`);
      } catch (e) {
        // ignore decode errors
      }
    }
  }, []);

  return (
    <DocumentTitle title="Account Suspended – Fortress">
      <Box>
        {/* Top app bar — mirrors the real nav bar styling including env color override */}
        <Box
          component="header"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: getAppBarBgColor(environmentName),
            height: 64,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            boxShadow: 3,
          }}
        >
          <Stack direction="row" alignItems="center" width="100%">
            <PropertySearchMenu
              selectedProperty={selectedProperty}
              userProperties={userProperties}
              onSelectProperty={onSelectProperty}
              onSelectPortfolioSummary={onSelectPortfolioSummary}
            />
            <Box sx={{ flexGrow: 1 }} />
            <UserMenu
              userMenuAnchorRef={userMenuAnchorRef}
              userMenuOpen={userMenuOpen}
              handleUserMenuToggle={handleUserMenuToggle}
              handleUserMenuClose={handleUserMenuClose}
              logout={logOut}
              fullName={fullName}
              isXs={isXs}
            />
          </Stack>
        </Box>

        {/* Page content */}
        <Box
          sx={{
            width: '100vw',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            px: 2,
            pt: '64px',
            backgroundColor: 'background.default',
          }}
        >
          <Box
            component="img"
            src={FortressLogo}
            alt="Fortress"
            sx={{ width: 200, mb: 2 }}
          />

          <Stack spacing={2} alignItems="center" sx={{ maxWidth: 520 }}>
            <Typography variant="titleLarge" align="center">
              Account Suspended
            </Typography>

            <Typography
              variant="bodyMedium"
              align="center"
              color="text.secondary"
            >
              Access to{' '}
              <strong>{selectedProperty?.name ?? 'this property'}</strong> has
              been suspended due to an outstanding balance.
            </Typography>

            <Typography
              variant="bodyMedium"
              align="center"
              color="text.secondary"
            >
              Please contact your company's Fortress administrator or account
              owner for assistance in resolving this balance with Fortress.
            </Typography>

            <Typography
              variant="bodyMedium"
              align="center"
              color="text.secondary"
            >
              If you have questions regarding this suspension, please contact
              Fortress Support at{' '}
              <Link href="mailto:support@fortress-technology.atlassian.net">
                support@fortress-technology.atlassian.net
              </Link>
              . Please note that Fortress Support cannot restore access until
              the outstanding balance has been paid in full.
            </Typography>
          </Stack>
        </Box>
      </Box>
    </DocumentTitle>
  );
};

export default AccountSuspendedPage;
